import { ImageOptimizerUrlBuilder } from '@creative/image-optimizer';
import { diInject } from '@di/di';
import { CreativeMode } from '@domain/creative/environment';
import { InternalWidgetEvent, WIDGET_PROPERTY_PREFIX } from '@domain/widget';
import { CreativeEvent, TimelineEvent, WidgetEvent } from '@domain/widget-events';
import { addColorSchemeMetaTag, emptyIframeSrc } from '@studio/utils/ad/dom';
import { cloneDeep } from '@studio/utils/clone';
import { b64DecodeUnicode } from '@studio/utils/encoding';
import { handleError, WidgetError } from '@studio/utils/errors';
import { EventEmitter } from '@studio/utils/event-emitter';
import { concatUrl, isInLocalEnvironment, isVideoFileUrl } from '@studio/utils/url';
import { isBase64 } from '@studio/utils/utils';
import { isValidUrl } from '@studio/utils/validation';
import { toRGBA } from '../../color.utils';
import { isFeedValue } from '../../elements/feed/feeds.utils';
import { getFontServiceParamsFromString } from '../../font-service.utils';
import { deserializeVersionedText } from '../../serialization/text-serializer';
import { isVersionedText } from '../rich-text/utils';
import { cssReset } from './css-reset';
import { widgetErrorHandler } from './widget-error-handler';
/**
 * Public properties that are usable by widgets should also be defined in ./declarations/widget.ts
 * Certain exposed functions are defined in widget-editor.component.ts
 */
export class Widget extends EventEmitter {
    set mouseInteractions(val) {
        this._mouseInteractions = val;
        // Iframes "steal" mouse events so always show the blocker element in the Design view
        if (!this._mouseInteractions || this._creativeApi.environment === 'design-view') {
            this._blockerElement.style.display = 'block';
        }
        else {
            this._blockerElement.style.display = 'none';
        }
    }
    get mouseInteractions() {
        return this._mouseInteractions;
    }
    set hidden(val) {
        this._hidden = val;
        if (this._elementNode.__rootElement) {
            if (this._hidden) {
                this._elementNode.__rootElement.style.display = 'none';
            }
            else {
                this._elementNode.__rootElement.style.display = 'block';
            }
        }
    }
    get hidden() {
        return this._hidden;
    }
    set cursor(value) {
        this._cursor = value;
        this._blockerElement.style.cursor = value;
        this._widgetIframeDocument.body.style.cursor = value;
    }
    get cursor() {
        return this._cursor;
    }
    constructor(_elementNode, _dataNode, _widgetRenderer, _env) {
        super();
        this._elementNode = _elementNode;
        this._dataNode = _dataNode;
        this._widgetRenderer = _widgetRenderer;
        this._env = _env;
        this.properties = {};
        this.time = this._elementNode.time;
        this.duration = this._elementNode.duration;
        this._previousWidgetState = {};
        this._mouseInteractions = false;
        this._feedPropertyCallbacks = new Map();
        this._animator = diInject(3 /* Token.ANIMATOR */);
        this._hidden = false;
        this._ad = diInject(0 /* Token.AD */);
        this._adApi = diInject(6 /* Token.AD_API */);
        this._creativeApi = diInject(7 /* Token.CREATIVE_API */);
        this._timelineApi = diInject(8 /* Token.TIMELINE_API */);
        this._feedStore = diInject(4 /* Token.FEED_STORE */, { optional: true });
        this._onWidgetMouseEvent = (mouseEvent) => {
            const event = mouseEvent.type;
            // Added to correct positions for heatmaps
            const mousePositionEvent = {
                x: mouseEvent.clientX,
                y: mouseEvent.clientY,
                translatedX: this.x + mouseEvent.clientX,
                translatedY: this.y + mouseEvent.clientY
            };
            this.emit(event, mousePositionEvent);
            /**
             * Calculate the mouse position in the creative relative to the widget iframe.
             * We need to manually emit the correct position as the iframe will "steal" the
             * interactions if mouseInteractions is true.
             */
            if (this.mouseInteractions) {
                const offsetX = Math.round(this.x + mouseEvent.clientX);
                const offsetY = Math.round(this.y + mouseEvent.clientY);
                this._creativeApi.emit(event, { x: offsetX, y: offsetY });
            }
        };
        this._onBlockerMouseEvent = (mouseEvent) => {
            var _a;
            const event = mouseEvent.type;
            if (this._env.STUDIO_JS &&
                event === WidgetEvent.Click &&
                this._creativeApi.environment !== 'creative') {
                return;
            }
            if ((event.startsWith('mouse') || event === WidgetEvent.Click) &&
                this._timelineApi.isPlaying &&
                ((_a = window.bfstudio) === null || _a === void 0 ? void 0 : _a.activePage) !== 'MV') {
                mouseEvent.stopPropagation();
                if (event === WidgetEvent.Click) {
                    this._creativeApi.open(mouseEvent);
                }
            }
            this.emit(event, { x: mouseEvent.offsetX, y: mouseEvent.offsetY });
        };
        this._feedDataChanged = (feed) => {
            for (const property in this.properties) {
                const prop = this.properties[property];
                if (!prop || prop.constructor.name !== 'Object' || typeof prop !== 'object') {
                    continue;
                }
                // Is a widget feed property
                if ('change' in prop && 'load' in prop && 'data' in prop) {
                    const feedId = typeof feed === 'string' ? feed : feed.id;
                    const feedProperty = prop;
                    if (!feedProperty.id || feedId !== feedProperty.id) {
                        return;
                    }
                    this._feedPropertyCallbacks.forEach(async (callback, id) => {
                        if (id === feedId) {
                            const data = await this._getFeedPropertyData(feedProperty);
                            callback(data);
                        }
                    });
                }
            }
        };
        this._tryInvokeFunction = (fn, catchFn) => {
            try {
                fn();
            }
            catch (e) {
                this._widgetErrorHandler(e);
                catchFn === null || catchFn === void 0 ? void 0 : catchFn(e);
            }
        };
        this._widgetErrorHandler = (e) => {
            var _a;
            const error = !(e instanceof WidgetError)
                ? new WidgetError(this._getWidgetErrorId(), { originalError: e })
                : e;
            handleError(error.message, error.contexts);
            (_a = this._isLoadedPromiseRejector) === null || _a === void 0 ? void 0 : _a.call(this, error);
        };
        this._dataNode.__widget = this;
        this.x = this._elementNode.x;
        this.y = this._elementNode.y;
        this.id = this._elementNode.id;
        this.width = this._elementNode.width;
        this.height = this._elementNode.height;
        this._adApi = this._adApi;
        this._creativeApi = this._creativeApi;
        this._timelineApi = this._timelineApi;
        if (this._feedStore) {
            this._feedStore.on('dataChanged', this._feedDataChanged);
        }
        this.isInitialized_m = new Promise((resolve, reject) => {
            this._isInitializedResolver = resolve;
            if (this._env.MODE === CreativeMode.ManageView) {
                this._isInitializedRejecter = reject;
            }
        });
        Object.assign(this._previousWidgetState, this);
        this._onViewNodeChange();
        this._createWidgetIframe(iframe => this._tryInvokeFunction(() => this._init(iframe), this._isInitializedRejecter));
    }
    async _init(iframe) {
        if (!iframe.contentWindow) {
            this._elementNode.__rootElement.removeChild(this._blockerElement);
            this._elementNode.__rootElement.removeChild(this._widgetIframe);
            return;
        }
        this._inSandboxedIframe = !iframe.contentDocument;
        this.properties = this._mapCustomProperties(this._elementNode);
        this._widgetIframeWindow = iframe.contentDocument
            ? iframe.contentWindow.window
            : window;
        this._widgetIframeWindow.Widget = this;
        this._widgetIframeWindow.WidgetEvent = WidgetEvent;
        this._widgetIframeWindow.CreativeEvent = CreativeEvent;
        this._widgetIframeWindow.TimelineEvent = TimelineEvent;
        this._widgetIframeWindow.Creative = this._creativeApi;
        this._widgetIframeWindow.Timeline = this._timelineApi;
        this._widgetIframeWindow.Ad = this._adApi;
        this._widgetIframeWindow.handleError = this._widgetErrorHandler;
        /** Currently only old snapshots requires decoding */
        const html = isBase64(this._dataNode.html)
            ? b64DecodeUnicode(this._dataNode.html)
            : this._dataNode.html;
        const css = isBase64(this._dataNode.css)
            ? b64DecodeUnicode(this._dataNode.css)
            : this._dataNode.css;
        const js = isBase64(this._dataNode.js)
            ? b64DecodeUnicode(this._dataNode.js)
            : this._dataNode.js;
        const htmlCode = html.replace(/bfstudio.blob.core.windows.net/g, 'c.bannerflow.net');
        let cssCode = css.replace(/bfstudio.blob.core.windows.net/g, 'c.bannerflow.net');
        const jsCode = js.replace(/bfstudio.blob.core.windows.net/g, 'c.bannerflow.net');
        if (this._inSandboxedIframe) {
            this._widgetIframe.remove();
            this._elementNode.__rootElement.classList.add(`widget-${this._elementNode.id}`);
            cssCode = this._toScopedCSS(cssCode);
            this._widgetIframeDocument = this._widgetIframeWindow.document;
            this._elementNode.__rootElement.innerHTML = htmlCode;
        }
        else {
            this._widgetIframeWindow.document.body.innerHTML = htmlCode;
            this._widgetIframeDocument = this._widgetIframe.contentDocument;
        }
        // Needs to be added after updating the content of the iframe
        addColorSchemeMetaTag(this._widgetIframe);
        if (htmlCode) {
            await this._loadScriptReferences(htmlCode);
        }
        const evalstr = this._inSandboxedIframe ? '' : widgetErrorHandler;
        this._tryInvokeFunction(() => {
            this._widgetIframeWindow.eval(`${evalstr + jsCode}\n//# sourceURL=widget-${this._elementNode.id}.js`);
        }, this._isInitializedRejecter);
        this._widgetIframeWindow.document.dispatchEvent(new Event('DOMContentLoaded', {
            bubbles: true,
            cancelable: true
        }));
        const style = this._widgetIframeWindow.document.createElement('style');
        style.innerHTML = cssReset + cssCode;
        this._widgetIframeWindow.document.head.appendChild(style);
        this._widgetIframeWindow.dispatchEvent(new Event('load', {
            bubbles: true,
            cancelable: true
        }));
        this._setMouseEvents();
        this._setTimelineAnimationEvents();
        this._tryInvokeFunction(() => this._dispatchInitEvents());
        this._isInitializedResolver();
        if (this.isLoadedPromise) {
            this._widgetRenderer.widgetLoadingPromises.push(new Promise((resolve, reject) => {
                this._isLoadedPromiseRejector = reject;
                this.isLoadedPromise
                    .then(resolve)
                    .catch(reject)
                    .finally(() => (this._isLoadedPromiseRejector = undefined));
            }));
        }
    }
    async _loadScriptReferences(htmlCode) {
        const scripts = this._widgetIframeWindow.document.body.querySelectorAll('script');
        const scriptPromises = [];
        for (const script of Array.from(scripts)) {
            if (htmlCode.indexOf(script.outerHTML) === -1) {
                continue;
            }
            if (script.src && (isInLocalEnvironment() || script.src.startsWith('https'))) {
                const s = this._widgetIframeWindow.document.createElement('script');
                s.src = script.src;
                let promiseResolve;
                scriptPromises.push(new Promise(resolve => {
                    promiseResolve = resolve;
                }));
                s.onload = () => {
                    promiseResolve();
                    s.remove();
                };
                this._widgetIframeWindow.document.body.appendChild(s);
            }
        }
        await Promise.all(scriptPromises);
    }
    _toScopedCSS(styleContent) {
        const doc = document.implementation.createHTMLDocument('');
        const styleElement = document.createElement('style');
        styleElement.textContent = styleContent;
        // the style will only be parsed once it is added to a document
        doc.body.appendChild(styleElement);
        const cssRules = Array.from(styleElement.sheet.cssRules);
        for (const rule of cssRules) {
            if (rule.selectorText) {
                const multiSelector = rule.selectorText.split(',');
                const scopedId = `widget-${this._elementNode.id}`;
                if (multiSelector.length) {
                    const newStyle = multiSelector.map(selector => {
                        if (selector.match(/html|body/g)) {
                            return selector
                                .replace('body', `body .${scopedId}`)
                                .replace('html', `html .${scopedId}`);
                        }
                        return `.${scopedId} ${selector}`;
                    });
                    rule.selectorText = newStyle.join(', ');
                }
                else {
                    rule.selectorText = `body ${rule.selectorText}`;
                }
            }
        }
        return cssRules.map(rule => rule.cssText).join('\n');
    }
    _dispatchInitEvents() {
        const ignoredEventTypes = [
            'mouse',
            'animation',
            'transition',
            WidgetEvent.Click,
            WidgetEvent.TCData,
            WidgetEvent.ShowPreloadImage,
            WidgetEvent.DurationChanged,
            WidgetEvent.TimeChanged
        ];
        Object.keys(WidgetEvent).forEach((event) => {
            if (!ignoredEventTypes.filter(e => event.match(new RegExp(e, 'gi'))).length) {
                this.emit(WidgetEvent[event]);
            }
        });
        this.emit(WidgetEvent.DurationChanged, this._elementNode.duration);
        this.emit(WidgetEvent.TimeChanged, this._elementNode.time);
        this._timelineApi.emit(TimelineEvent.Tick, this._timelineApi.currentTime);
    }
    _setMouseEvents() {
        Object.keys(WidgetEvent).forEach((event) => {
            if (event.startsWith('Mouse') || WidgetEvent[event] === WidgetEvent.Click) {
                this._blockerElement.addEventListener(WidgetEvent[event], this._onBlockerMouseEvent);
                this._widgetIframeWindow.addEventListener(WidgetEvent[event], this._onWidgetMouseEvent);
            }
        });
    }
    /**
     * The timeline events are added here instead of in TimelineApi as we need to
     * know that it's the specific widget that the event occurs for.
     */
    _setTimelineAnimationEvents() {
        Object.keys(WidgetEvent).forEach(event => {
            if (event.indexOf('Animation') > -1 || event.indexOf('Transition') > -1) {
                this._animator.on(WidgetEvent[event], (element) => {
                    if (element.id === this.id) {
                        this.emit(WidgetEvent[event], element);
                    }
                });
            }
        });
    }
    /**
     * Check if the values of the widget has been changed
     */
    _checkValueDiff(property, propertyToCheck) {
        function isObject(obj) {
            return obj != null && obj.constructor.name === 'Object';
        }
        function isFunction(val) {
            return val instanceof Function || typeof val === 'function';
        }
        if (property !== propertyToCheck) {
            return false;
        }
        if (property === 'customProperties') {
            for (const i in this.properties) {
                const newValue = this.properties[i];
                const prevValue = this._previousWidgetState.properties[i];
                if (isObject(newValue) &&
                    'load' in newValue &&
                    isObject(prevValue) &&
                    'load' in prevValue) {
                    if (newValue.id !== prevValue.id) {
                        this._previousWidgetState.properties[i] = this.properties[i];
                        return true;
                    }
                }
                else if (isObject(newValue) && isObject(prevValue)) {
                    if (Array.isArray(newValue)) {
                        for (let j = 0; j < newValue.length; j++) {
                            if (typeof prevValue[j] !== 'undefined' && newValue[j] !== prevValue[j]) {
                                return true;
                            }
                        }
                    }
                    else {
                        for (const value in newValue) {
                            if (isFunction(newValue[value])) {
                                continue;
                            }
                            if (!(value in prevValue)) {
                                return true;
                            }
                            else if (newValue[value] !== prevValue[value]) {
                                return true;
                            }
                        }
                    }
                }
                else if (newValue !== prevValue) {
                    this._previousWidgetState.properties[i] = this.properties[i];
                    return true;
                }
            }
        }
        else if (this._previousWidgetState[property] !== this[property]) {
            this._previousWidgetState[property] = this[property];
            return true;
        }
        return false;
    }
    /**
     * Maps the customProperties to a property list ({key: value}) e.g:
     *
     * { label: "label", type: "custom:key", unit: "string", value: "hello"} => { key: 'hello' }
     */
    _mapCustomProperties(viewElement) {
        const properties = {};
        const clonedCustomProperties = cloneDeep(viewElement.customProperties);
        if (clonedCustomProperties === null || clonedCustomProperties === void 0 ? void 0 : clonedCustomProperties.length) {
            clonedCustomProperties.forEach(property => {
                var _a;
                const key = property.name.replace(WIDGET_PROPERTY_PREFIX, '');
                const unit = property.unit;
                const isVersionedProperty = isVersionedText(property);
                const value = isVersionedProperty && typeof property.value === 'string'
                    ? deserializeVersionedText(property.value)
                    : property.value;
                if (unit === 'text') {
                    let text;
                    if (typeof value === 'object' &&
                        !Array.isArray(value) &&
                        'text' in value &&
                        typeof value.text === 'string') {
                        text = value.text;
                    }
                    else if (typeof value === 'string') {
                        text = value;
                    }
                    else {
                        return;
                    }
                    if (isVideoFileUrl(text)) {
                        const adTagParentPath = this._ad.getParentPath();
                        if (adTagParentPath && !text.includes('://')) {
                            text = concatUrl(adTagParentPath, text);
                        }
                    }
                    properties[key] = text;
                }
                else if (unit === 'select') {
                    properties[key] = (_a = value.find((val) => val.selected)) === null || _a === void 0 ? void 0 : _a.value;
                }
                else if (unit === 'color') {
                    // `property.value` is already serialized as string in generated creatives
                    properties[key] =
                        typeof property.value === 'string'
                            ? property.value
                            : toRGBA(property.value);
                }
                else if (unit === 'font') {
                    const fontProperty = value;
                    if (fontProperty.id && fontProperty.id !== 'undefined') {
                        // Need to prepend 'f-' to font-id, otherwise it's not a valid font face name.
                        fontProperty.id = `f-${value.id}`;
                        const fontSrc = fontProperty.src;
                        const isBase64Font = fontSrc.startsWith('data');
                        const fontServiceUri = isBase64Font || this._env.STUDIO_JS // Don't optimize font in Studio
                            ? fontSrc
                            : `${this._env.FONTSERVICE_API_ORIGIN}/api/v2/font?u=${encodeURIComponent(fontSrc)}`;
                        if (isValidUrl(fontServiceUri)) {
                            const url = new URL(fontServiceUri);
                            if (!isBase64Font) {
                                url.searchParams.set('r', this._elementNode.id + key);
                            }
                            fontProperty.src = url.toString();
                        }
                    }
                    // Used by widgets
                    fontProperty.toFontFace = (text, name) => {
                        if (name && !name.match(/^[a-z|A-Z]/)) {
                            throw new Error('Custom font name must start with a letter a-Z.');
                        }
                        let srcUrl = fontProperty.src;
                        if (!this._env.STUDIO_JS && !srcUrl.startsWith('data') && text) {
                            const { textParams } = getFontServiceParamsFromString(text);
                            if (textParams) {
                                srcUrl = srcUrl + textParams;
                            }
                        }
                        return `
                            @font-face {
                            font-family: "${name !== null && name !== void 0 ? name : fontProperty.id}";
                            src: url("${srcUrl}");
                        }`;
                    };
                    // Used by widgets
                    fontProperty.createFontFace = (text, name) => {
                        if (text !== undefined && typeof text !== 'string') {
                            throw new Error(`Text must be a string or undefined. "${typeof text}" was passed`);
                        }
                        if (name && !name.match(/^[a-z|A-Z]/)) {
                            throw new Error('Custom font name must start with a letter a-Z.');
                        }
                        let url = fontProperty.src;
                        if (!this._env.STUDIO_JS && !url.startsWith('data') && text) {
                            const { textParams } = getFontServiceParamsFromString(text);
                            if (textParams) {
                                url = url + textParams;
                            }
                        }
                        const fontId = name !== null && name !== void 0 ? name : fontProperty.id;
                        const fontFace = new FontFace(fontId, `url('${url}')`);
                        const fontFacePromise = new Promise(async (resolve, reject) => {
                            try {
                                const loadedFontFace = await fontFace.load();
                                this._widgetIframeDocument.fonts.add(loadedFontFace);
                                resolve(loadedFontFace);
                            }
                            catch (err) {
                                const widgetError = new WidgetError(this._getWidgetErrorId(), {
                                    reason: 'Could not load font face',
                                    originalError: err,
                                    fontUrl: url
                                });
                                reject(widgetError);
                            }
                        });
                        this._widgetRenderer.widgetLoadingPromises.push(fontFacePromise);
                        return fontFacePromise;
                    };
                    properties[key] = fontProperty;
                }
                else if (unit === 'image' && typeof value === 'object' && 'src' in value) {
                    let url;
                    if (value.src && value.src !== 'undefined') {
                        const adTagParentPath = this._ad.getParentPath();
                        if (adTagParentPath && !value.src.includes('://')) {
                            url = concatUrl(adTagParentPath, value.src);
                        }
                        else {
                            url = value.src;
                        }
                        properties[key] = url;
                    }
                    else {
                        properties[key] = undefined;
                    }
                }
                else if (unit === 'feed' && isFeedValue(value)) {
                    properties[key] = this._createFeedHelper(key, value);
                }
                else if (typeof value === 'boolean' || typeof value === 'number') {
                    properties[key] = value;
                }
            });
        }
        return properties;
    }
    _createFeedHelper(key, value) {
        const feedHelper = {
            loaded: false,
            load: {},
            data: {},
            id: value.id,
            change: {}
        };
        const propertyValue = this.properties[key];
        feedHelper.load = () => this._loadFeed(feedHelper, value);
        const currentCallback = this._feedPropertyCallbacks.get((propertyValue === null || propertyValue === void 0 ? void 0 : propertyValue.id) || '');
        if (currentCallback && typeof propertyValue.id !== 'undefined') {
            this._feedPropertyCallbacks.delete(propertyValue.id);
            this._feedPropertyCallbacks.set(feedHelper.id, currentCallback);
        }
        feedHelper.change = (callback) => {
            this._feedPropertyCallbacks.set(feedHelper.id, callback);
        };
        return feedHelper;
    }
    _loadFeed(feedHelper, feed) {
        const promise = new Promise(async (resolve, reject) => {
            var _a, _b;
            try {
                const id = feed === null || feed === void 0 ? void 0 : feed.id;
                if (!id) {
                    const widgetError = new WidgetError(this._getWidgetErrorId(), {
                        reason: 'Could not fetch feed. No feed is selected.'
                    });
                    reject(widgetError);
                    return;
                }
                if (feedHelper.loaded) {
                    feedHelper.data = (_b = (_a = this._feedStore) === null || _a === void 0 ? void 0 : _a.feeds.get(id)) === null || _b === void 0 ? void 0 : _b.feed;
                }
                else {
                    feedHelper.data = await this._feedStore.fetchFeedData(id, true);
                    feedHelper.loaded = true;
                }
                resolve(feedHelper.data ? cloneDeep(feedHelper.data) : undefined);
            }
            catch (_c) {
                const widgetError = new WidgetError(this._getWidgetErrorId(), {
                    reason: 'Could not fetch feed. No feed is selected.'
                });
                this._widgetErrorHandler(widgetError);
            }
        });
        this._widgetRenderer.widgetLoadingPromises.push(promise);
        return promise;
    }
    _onWidgetElementChanged(property) {
        // Sync the property value of the updated elementNode with values of the widget
        this._syncWidgetValueWithElementNodeValue(property);
        if (this._checkValueDiff('x', property) || this._checkValueDiff('y', property)) {
            this.emit(WidgetEvent.Move);
        }
        if (this._checkValueDiff('width', property) || this._checkValueDiff('height', property)) {
            this.emit(WidgetEvent.Resize);
        }
        if (this._checkValueDiff('customProperties', property)) {
            this.emit(WidgetEvent.PropertyChanged);
        }
        if (this._checkValueDiff('duration', property)) {
            this.emit(WidgetEvent.DurationChanged, this._elementNode.duration);
        }
        if (this._checkValueDiff('time', property)) {
            this.emit(WidgetEvent.TimeChanged, this._elementNode.time);
        }
        // Keep track of the current state for future change checks
        Object.assign(this._previousWidgetState, this);
    }
    /**
     * @param  {string} property
     * Updates the property values of the widget to match the values in the elementNode
     */
    _syncWidgetValueWithElementNodeValue(property) {
        if (property === 'customProperties') {
            this.properties = this._mapCustomProperties(this._elementNode);
        }
        else {
            if (!(property in this)) {
                return;
            }
            // Use the internal property rather than the one on elementNode
            if (property === 'hidden') {
                return;
            }
            this[property] = this._elementNode[property];
        }
    }
    getOptimizedImageUrl(url, options) {
        if (this._ad.isHtml5Export()) {
            return url;
        }
        // Return retina image if optimize is off in DV
        const dpr = ImageOptimizerUrlBuilder.getDevicePixelRatio(this.width, this.height, this._env.MODE, typeof options.quality === 'number' ? options.highDpi : true);
        return ImageOptimizerUrlBuilder.getUrl(url, Object.assign(Object.assign({}, options), { dpr }));
    }
    emitCustomEvent(event, data) {
        this._creativeApi.emit(`customEvent:${event}`, data);
    }
    onCustomEvent(event, callback) {
        this._creativeApi.on(`customEvent:${event}`, data => {
            callback(data);
        });
    }
    removeCustomEvent(event, callback) {
        this._creativeApi.off(`customEvent:${event}`, callback);
    }
    _onViewNodeChange() {
        this.on(InternalWidgetEvent.ViewNodeChanged, (viewNode) => {
            this._tryInvokeFunction(() => {
                for (const property in viewNode) {
                    this._onWidgetElementChanged(property);
                }
            });
        });
    }
    _createWidgetIframe(callback) {
        const iframe = document.createElement('iframe');
        let timeout;
        iframe.addEventListener('load', () => {
            if (timeout) {
                clearTimeout(timeout);
            }
            timeout = window.setTimeout(() => {
                callback(iframe);
            }, 0);
        });
        const style = iframe.style;
        iframe.id = `widget-${this._elementNode.id}`;
        style.maxHeight = style.height = style.width = '100%';
        style.filter = style.transform = style.border = style.outline = 'none';
        style.display = 'block';
        style.position = 'absolute';
        style.top = style.left = '0';
        style.transformOrigin = '0 0';
        iframe.src = emptyIframeSrc();
        // Used to catch interactions if mouseInteractions is off
        const blocker = document.createElement('div');
        const blockerStyle = blocker.style;
        blockerStyle.position = 'absolute';
        blockerStyle.top = blockerStyle.left = '0px';
        blocker.classList.add('widget-interaction-blocker');
        blockerStyle.width = blockerStyle.height = '100%';
        blockerStyle.display = 'block';
        blockerStyle.zIndex = '1';
        this._blockerElement = blocker;
        this._widgetIframe = iframe;
        this._elementNode.__rootElement.appendChild(iframe);
        this._elementNode.__rootElement.appendChild(blocker);
    }
    _getFeedPropertyData(feedProperty) {
        return feedProperty.load();
    }
    _getWidgetErrorId() {
        return this._dataNode.name || this._dataNode.id;
    }
    destroy() {
        var _a;
        this.clearEvents();
        (_a = this._feedStore) === null || _a === void 0 ? void 0 : _a.off('dataChanged', this._feedDataChanged);
        this._feedPropertyCallbacks.clear();
        try {
            Object.keys(WidgetEvent).forEach((event) => {
                if (event.startsWith('Mouse') || WidgetEvent[event] === WidgetEvent.Click) {
                    this._blockerElement.removeEventListener(WidgetEvent[event], this._onBlockerMouseEvent);
                    this._widgetIframeWindow.removeEventListener(WidgetEvent[event], this._onWidgetMouseEvent);
                }
            });
            Object.keys(WidgetEvent).forEach((event) => {
                if (event.indexOf('Animation') > -1 || event.indexOf('Transition') > -1) {
                    this._animator.off(WidgetEvent[event]);
                }
            });
        }
        catch (_b) {
            /* empty */
        }
    }
}
